import React from "react";
import { TextFieldVariant, Typeahead } from "@merge-api/merge-javascript-shared";
import {
  ConditionType,
  LinkedAccountCondition,
  LinkedAccountConditionMeta,
} from "../../../../../../../../models/Entities";
import EnumValueSelect from "./EnumValueSelect";
import ValueTextField from "./ValueTextField";
import { getEnumValueOptions, getOperatorValue } from "../helpers";

type GenericValueFieldProps = {
  linkedAccountCondition: Partial<LinkedAccountCondition>;
  linkedAccountConditionMeta: LinkedAccountConditionMeta;
  onLinkedAccountConditionChange: (LinkedAccountCondition: Partial<LinkedAccountCondition>) => void;
};

const BOOLEAN_OPTIONS: boolean[] = [true, false];

const GenericValueField = ({
  linkedAccountCondition,
  linkedAccountConditionMeta,
  onLinkedAccountConditionChange,
}: GenericValueFieldProps) => {
  const operatorValue = getOperatorValue(linkedAccountCondition, linkedAccountConditionMeta);
  const enumValueOptions = getEnumValueOptions(linkedAccountCondition, linkedAccountConditionMeta);

  if (operatorValue?.key_type === ConditionType.BOOLEAN) {
    // value can be a real boolean stored in JSON; coerce string forms so older saved conditions still match an option
    const rawValue = linkedAccountCondition.value as boolean | string | string[] | undefined;
    const value: boolean | undefined =
      typeof rawValue === "boolean"
        ? rawValue
        : rawValue === "true"
        ? true
        : rawValue === "false"
        ? false
        : undefined;

    return (
      <Typeahead
        key="boolean-value"
        className="h-full py-[3px]"
        borderVariant={TextFieldVariant.InlinedRounded}
        placeholder="Select value..."
        options={BOOLEAN_OPTIONS}
        disableClearable
        getOptionLabel={(option) => (option ? "true" : "false")}
        value={value}
        onChange={(_: any, valueOption: boolean | null) => {
          onLinkedAccountConditionChange({
            ...linkedAccountCondition,
            // backend JSONField round-trips real booleans; cast because the shared ConditionValueType is string|string[]
            value: (valueOption ?? undefined) as unknown as string,
          });
        }}
      />
    );
  }

  return enumValueOptions && enumValueOptions.length > 0 ? (
    <EnumValueSelect
      key="enum-value"
      linkedAccountCondition={linkedAccountCondition}
      onLinkedAccountConditionChange={onLinkedAccountConditionChange}
      enumValueOptions={enumValueOptions}
    />
  ) : (
    <ValueTextField
      key="value"
      linkedAccountCondition={linkedAccountCondition}
      linkedAccountConditionMeta={linkedAccountConditionMeta}
      onLinkedAccountConditionChange={onLinkedAccountConditionChange}
    />
  );
};

export default GenericValueField;
